import { APColumn, APRow, APText, APPalette, APButton, APImage, APSizedBox, APExpanded, APSwitcher, useDeviceType, showSnackbar } from "ap-components";
import { useRef, useState } from "react";
import CalculatorInput from "../CalculatorInput";
import CalculatorFormulas from "../CalculatorFormulas";
import { ChartValue, ScreenShot } from "./Emi";
import { APCharts } from "../../../Common/Charting";
import saveAs from "file-saver";
import html2canvas from "html2canvas";

export default function RegularIncome() {
    const [calculatorType, setCalculatorType] = useState<string>('No. of years')
    const targetElementRef = useRef<HTMLDivElement>(null);
    const deviceType = useDeviceType();
    const in1Ref = useRef<HTMLDivElement>(null);
    const in2Ref = useRef<HTMLDivElement>(null);
    const in3Ref = useRef<HTMLDivElement>(null);
    const in4Ref = useRef<HTMLDivElement>(null);
    const in5Ref = useRef<HTMLDivElement>(null);


    const [initialValue, setInitialValue] = useState({
        sipAmount: 10000,
        lumpsum: 2500000,
        investmentPeriod: 20,
        expectedReturn: 12,
        waitingPeriodBeforeWithdrawal: 10,
        howLongYouWantToWithdraw: 20,
        expectedReturnInWithdrawalPeriod: 5,
        monthlyWithdrawalRequirement: 25000
    });

    var portfolioValue: number = 0;
    if ((initialValue.investmentPeriod + initialValue.waitingPeriodBeforeWithdrawal + initialValue.howLongYouWantToWithdraw) <= 2) {
        let months = ((initialValue.investmentPeriod + initialValue.waitingPeriodBeforeWithdrawal) * 12) ?? 0;
        var data = CalculatorFormulas.monthlyInvestmentPortfolioCalculation({ months, expectedReturnRate: initialValue.expectedReturn, sipAmount: initialValue.sipAmount, lumpsumAmount: initialValue.lumpsum })
        portfolioValue = data[data.length - 1].portfolioValue ?? 0
    } else {
        portfolioValue = CalculatorFormulas.getPortFolioValue(
            { SIPAmount: initialValue.sipAmount, lumpsumAmount: initialValue.lumpsum, investmentPeriod: initialValue.investmentPeriod, expectedReturnRate: initialValue.expectedReturn }
        ) ?? 0
    }


    var portfolioValueBeforeWithdrawal = CalculatorFormulas.calculateInvestmentValue(
        {
            expectedReturnRate: initialValue.expectedReturn,
            corpusAtEnd: portfolioValue ?? 0,
            waitingPeriodYears: initialValue.waitingPeriodBeforeWithdrawal
        })

    let monthlyWithdrawal = 0;

    let depleteTime: { status: string | undefined, value: number | undefined } = { status: undefined, value: undefined };

    if (calculatorType.includes('SWP')) {
        var initValue = {
            corpusAmount: portfolioValueBeforeWithdrawal,
            swp: initialValue.monthlyWithdrawalRequirement,
            withdrawalRate: initialValue.expectedReturnInWithdrawalPeriod,
        }
        depleteTime = CalculatorFormulas.getDepleteTime(initValue);
    }
    else {
        monthlyWithdrawal = CalculatorFormulas.calculateMonthlyWithdrawal({ corpusBeforeWithdrawal: portfolioValueBeforeWithdrawal, withdrawalYears: initialValue.howLongYouWantToWithdraw, returnExpected: initialValue.expectedReturnInWithdrawalPeriod })
    }
    var out: { portfolioValue: number | undefined; time: number | undefined; }[] = [];
    if (!calculatorType.includes('SWP')) {
        out = CalculatorFormulas.regularIncomeCalculationForYears
            ({
                sipAmount: initialValue.sipAmount,
                lumpsumAmount: initialValue.lumpsum,
                investmentPeriod: initialValue.investmentPeriod,
                expectedReturnRate: initialValue.expectedReturn,
                waitingPeriodBeforeWithdrawal: initialValue.waitingPeriodBeforeWithdrawal,
                withdrawalYears: initialValue.howLongYouWantToWithdraw,
                monthlyWithdrawal:
                    monthlyWithdrawal ?? 0,
                expectedReturnValue: initialValue.expectedReturnInWithdrawalPeriod,

            });

    } else {
        out = CalculatorFormulas.regularIncomeCalculationForSWP
            ({
                sipAmount: initialValue.sipAmount,
                lumpsumAmount: initialValue.lumpsum,
                investmentPeriod: initialValue.investmentPeriod,
                expectedReturnRate: initialValue.expectedReturn,
                waitingPeriodBeforeWithdrawal: initialValue.waitingPeriodBeforeWithdrawal,
                withdrawalYears: initialValue.howLongYouWantToWithdraw,
                depleteTime: depleteTime?.value != undefined ? depleteTime?.value : undefined,
                monthlyWithdrawal:
                    initialValue.monthlyWithdrawalRequirement,
                expectedReturnValue: initialValue.expectedReturnInWithdrawalPeriod,

            });

    }

    async function captureScreenshot() {
        if (targetElementRef.current) {
            try {
                in1Ref.current?.style.setProperty("display", "none", "important");
                in2Ref.current?.style.setProperty("display", "block", "important");
                in3Ref.current?.style.setProperty("display", "none", "important");
                in4Ref.current?.style.setProperty("display", "block", "important");
                in5Ref.current?.style.setProperty("display", "block", "important");

                const canvas = await html2canvas(targetElementRef.current);
                const url = canvas.toDataURL();
                saveAs(url, "Regular income Calculator")
            } catch (error) {
                console.error('Error capturing screenshot:', error);
                showSnackbar('Could not create the snapshot. Please try again.', 'negative');
            } finally {
                // Restore the live view in finally — a throw from html2canvas (likely on phones)
                // must not leave the calculator stuck in snapshot mode with no way back.
                in1Ref.current?.style.setProperty("display", "block", "important");
                in2Ref.current?.style.setProperty("display", "none", "important");
                in3Ref.current?.style.setProperty("display", "block", "important");
                in4Ref.current?.style.setProperty("display", "none", "important");
                in5Ref.current?.style.setProperty("display", "none", "important");
            }
        }
    };


    var option = {
        legend: {
            show: true,
            left: "3%"
        },
        grid: {
            left: '12%',
            bottom: "15%",
            right: "5%"
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'none',
            },
        },
        xAxis: [
            {
                type: 'category',
                data: (out as { portfolioValue: number; time: number; }[])
                    .map(year => year.time),
                axisPointer: {
                    type: 'shadow',
                    label: {
                        formatter: (v: any) => {
                            if (calculatorType.includes('SWP')) {
                                if (depleteTime?.value != undefined && depleteTime?.value < 12 && (initialValue.investmentPeriod + initialValue.waitingPeriodBeforeWithdrawal) <= 1) {
                                    return "Month " + v.value;
                                }
                            } else {
                                if ((initialValue.investmentPeriod + initialValue.waitingPeriodBeforeWithdrawal + initialValue.howLongYouWantToWithdraw) <= 2) {
                                    return "Month " + v.value;
                                }
                            }
                            return "Year " + v.value;
                        }
                    }
                },
                axisLabel: {
                    formatter:
                        depleteTime?.value != undefined ?
                            (calculatorType.includes('SWP') && depleteTime.value < 12 && (initialValue.investmentPeriod + initialValue.waitingPeriodBeforeWithdrawal) <= 1 ?
                                `Month {value}` :
                                `Year {value}`) :
                            ((initialValue.investmentPeriod + initialValue.waitingPeriodBeforeWithdrawal + initialValue.howLongYouWantToWithdraw) <= 2 ?
                                `Month {value}` :
                                `Year {value}`)
                }
            }
        ],
        yAxis: [
            {
                type: 'value',
                min: 0,
                axisLabel: {
                    width: "10px",
                    position: "right",
                    formatter: (v: number) => {
                        return v.currency(1, true);
                    }
                }
            },
        ],
        position: "left",
        series: [
            {
                name: 'Portfolio Value',
                type: 'bar',
                stack: 'maturity',
                itemStyle: {
                    color: '#268EFF'
                },
                tooltip: {
                    valueFormatter: function (value: number) {
                        return value.currency(2, true);
                    }
                },
                data: out ? out?.map((portfolioValue: any) => portfolioValue.portfolioValue ?? 0) : [],
            },
        ]
    }


    return (
        <div style={{ padding: "8px" }} ref={targetElementRef}>
            <APRow mainAxisAlignment="spaceBetween" >
                <APText variant="h2" style={{ color: APPalette["grey-700"] }}>Regular Income Calculator</APText>
                <div ref={in4Ref} style={{ height: "16px", display: "none" }}>
                    <APImage src='/assetplus/aplight.svg' height="16px" />
                </div>
                <div ref={in3Ref}>
                    <APButton
                        type="secondary"
                        size="medium"
                        style={{ opacity: 1 }}
                        onClick={captureScreenshot}
                    >
                        <APImage src="/icons/icon-camera.svg" />
                        <APText variant="button-small" style={{ color: APPalette["brand-300"], opacity: 1 }}>Take Snapshot</APText>
                    </APButton>
                </div>
            </APRow>
            <APSizedBox height="8px" />
            <APText variant="paragraph-small" style={{ color: APPalette["grey-600"] }}>
                Check out how much you can withdraw or how long your money will last for funding your retirement or other expenses.
                Simply enter the amount you can invest, the expected return, and the period you can stay invested.
            </APText>
            <APSizedBox height="16px" />
            <div style={{ display: "flex", flexDirection: deviceType == "mobile" ? "column-reverse" : "row" }}>
                <APExpanded flex={2}>
                    <div style={{ paddingTop: "16px", display: "flex", flexDirection: "column" }} ref={in1Ref}>
                        <APRow mainAxisAlignment='spaceBetween'><APText>Plan Income By</APText>
                            <APSwitcher
                                width="200px"
                                options={["No. of years", "Monthly SWP",]}
                                onSelect={(type) => {
                                    setCalculatorType(type)
                                }}
                                initial={calculatorType}
                                variant="primary"
                            />
                        </APRow>
                        <APSizedBox height="24px" />
                        <CalculatorInput
                            label="SIP"
                            dataType="amount"
                            value={initialValue.sipAmount}
                            sliderMax={200000}
                            inputMax={100000000}
                            inputMin={0}
                            sliderMin={10000}
                            sliderStep={10000}
                            onChange={(value: any) => {
                                setInitialValue({ ...initialValue, sipAmount: value });
                            }}
                        />
                        <APSizedBox height="24px" />
                        <CalculatorInput
                            label="Lumpsum"
                            dataType="amount"
                            value={initialValue.lumpsum}
                            sliderMax={20000000}
                            inputMax={100000000}
                            inputMin={0}
                            sliderMin={10000}
                            sliderStep={10000}
                            onChange={(value: any) => {
                                setInitialValue({ ...initialValue, lumpsum: value });
                            }}
                        />
                        <APSizedBox height="24px" />
                        <CalculatorInput
                            label="Investment Period"
                            dataType="age"
                            value={initialValue.investmentPeriod ?? 1}
                            sliderMax={30}
                            inputMin={1}
                            inputMax={80}
                            sliderMin={1}
                            sliderStep={1}
                            onChange={(value) => {
                                setInitialValue({ ...initialValue, investmentPeriod: value });
                            }}
                        />
                        <APSizedBox height="24px" />
                        <CalculatorInput
                            label="Expected Returns"
                            dataType="percentage"
                            value={initialValue.expectedReturn}
                            sliderMax={30}
                            inputMin={1}
                            inputMax={30}
                            sliderMin={1}
                            sliderStep={1}
                            onChange={(value: any) => {
                                setInitialValue({ ...initialValue, expectedReturn: value });
                            }}
                        />
                        <APSizedBox height="24px" />
                        <CalculatorInput
                            label="waiting period before withdrawal"
                            dataType='age'
                            value={initialValue.waitingPeriodBeforeWithdrawal}
                            sliderMax={30}
                            inputMin={0}
                            inputMax={50}
                            sliderMin={0}
                            sliderStep={1}
                            onChange={(value: any) => {
                                setInitialValue({ ...initialValue, waitingPeriodBeforeWithdrawal: value });
                            }}
                        />
                        <APSizedBox height="24px" />
                        {!calculatorType.includes('SWP') ? <CalculatorInput
                            key={calculatorType}
                            label={"how long you want to withdraw"}
                            dataType={"age"}
                            value={initialValue.howLongYouWantToWithdraw}
                            sliderMax={30}
                            inputMin={0}
                            inputMax={100}
                            sliderMin={1}
                            sliderStep={1}
                            onChange={(value: any) => {
                                setInitialValue({ ...initialValue, howLongYouWantToWithdraw: value });
                            }}
                        /> :
                            <CalculatorInput
                                key={calculatorType}
                                label={'Monthly Withdrawal'}
                                dataType={'amount'}
                                value={initialValue.monthlyWithdrawalRequirement}
                                sliderMax={1000000}
                                inputMin={100}
                                inputMax={100000000}
                                sliderMin={1000}
                                sliderStep={1000}
                                onChange={(value: any) => {
                                    setInitialValue({ ...initialValue, monthlyWithdrawalRequirement: value });
                                }}
                            />}
                        <APSizedBox height="24px" />
                        <CalculatorInput
                            label="EXP returns in withdrawal period"
                            dataType="percentage"
                            value={initialValue.expectedReturnInWithdrawalPeriod}
                            sliderMax={30}
                            inputMin={1}
                            inputMax={30}
                            sliderMin={1}
                            sliderStep={1}
                            onChange={(value: any) => {
                                setInitialValue({ ...initialValue, expectedReturnInWithdrawalPeriod: value });
                            }}
                        />
                    </div>
                </APExpanded>
                <APExpanded flex={2}>
                    <div style={{ paddingTop: "16px", display: "none", flexDirection: "column" }} ref={in2Ref}>
                        <ScreenShot label={`SIP`} value={(initialValue.sipAmount).currency(2, true)} />
                        <APSizedBox height="24px" />
                        <ScreenShot label={`Lumpsum`} value={(initialValue.lumpsum).currency(2, true)} />
                        <APSizedBox height="24px" />
                        <ScreenShot label={`Investment Period`} value={(initialValue.investmentPeriod)} />
                        <APSizedBox height="24px" />
                        <ScreenShot label={`Expected returns`} value={(initialValue.expectedReturn)} />
                        <APSizedBox height="24px" />
                        <ScreenShot label={`Waiting Period Before Withdrawal`} value={(initialValue.waitingPeriodBeforeWithdrawal)} />
                        <APSizedBox height="24px" />

                        <ScreenShot label={`Expected return in withdrawal period`} value={(initialValue.expectedReturnInWithdrawalPeriod)} />
                        <APSizedBox height="24px" />
                        {!calculatorType.includes('SWP') ?
                            <ScreenShot label={`How long you want to withdraw`} value={(initialValue.howLongYouWantToWithdraw)} /> :
                            <ScreenShot label={`Monthly Withdrawal`} value={(initialValue.monthlyWithdrawalRequirement)} />

                        }
                    </div>
                </APExpanded>
                <APSizedBox width="32px" />
                <APExpanded flex={3}>
                    <APColumn crossAxisAlignment="stretch" style={{ padding: "16px", background: APPalette["grey-50"], borderRadius: "16px" }} >
                        <APCharts height="320px" option={option} />
                        <APColumn mainAxisAlignment="spaceBetween">
                            < ChartValue label={`Portfolio Value at the end of ${getOrdinal(initialValue.investmentPeriod)} Year`} value={portfolioValue ?? 0} style={{ color: "#268EFF" }} />
                            <APSizedBox height="8px" />
                            < ChartValue label={"Portfolio Value before withdrawal Begins"} value={portfolioValueBeforeWithdrawal} style={{ color: APPalette["grey-700"] }} />
                            <APSizedBox height="8px" />
                            {!calculatorType.includes('SWP') ? < ChartValue label={`Monthly Withdrawal for ${initialValue.howLongYouWantToWithdraw} Years`} value={Number.isFinite(monthlyWithdrawal) ? monthlyWithdrawal : 0} style={{ color: "#5DD27A" }} /> :
                                <APSizedBox >
                                    <APColumn crossAxisAlignment="center" gap="6px" >
                                        <APText color={APPalette['grey-600']} variant='paragraph-small'>How long you can keep withdrawing</APText>
                                        <APColumn crossAxisAlignment="center">
                                            <APText center={true} variant='h2'>
                                                {initialValue.monthlyWithdrawalRequirement > portfolioValueBeforeWithdrawal ? '0 Months' : `${depleteTime?.value == undefined ? depleteTime?.status : `~ ${depleteTime?.status}`}`}
                                            </APText>
                                        </APColumn>
                                    </APColumn>
                                </APSizedBox>
                            }
                            <APSizedBox height="8px" />
                            <APRow mainAxisSize="min" style={{ maxWidth: '406px' }}>
                                <APText center variant='paragraph-large' color={APPalette["grey-700"]}>
                                    With a total investment of
                                    <span style={{ color: APPalette["grey-900"], fontWeight: 600 }}>
                                        {` ${(initialValue.lumpsum + (initialValue.sipAmount * 12 * initialValue.investmentPeriod)).currency(2, true)} `}
                                    </span>
                                    over <span style={{ color: APPalette["grey-900"], fontWeight: 600 }} >{` ${initialValue.investmentPeriod}`}</span> years,
                                    {initialValue.monthlyWithdrawalRequirement > portfolioValueBeforeWithdrawal ? ' your corpus won\'t be enough to withdraw' : ' you will be able to withdraw'}
                                    <span style={{ color: APPalette["grey-900"], fontWeight: 600 }}>
                                        {` ${!calculatorType.includes('SWP') ?
                                            (monthlyWithdrawal !== undefined && monthlyWithdrawal.currency(2, true)) :
                                            initialValue.monthlyWithdrawalRequirement.currency(2, true)} `}
                                    </span>
                                    every month {
                                        initialValue.monthlyWithdrawalRequirement > portfolioValueBeforeWithdrawal ?
                                            '. Consider investing more to achieve your monthly withdrawal goals'
                                            :
                                            <>
                                                {depleteTime?.status != 'For Life' && <span>{'for approximately'}</span>}
                                                <span style={{ color: APPalette["grey-900"], fontWeight: 600 }} >
                                                    {` ${!calculatorType.includes('SWP') ?
                                                        initialValue.howLongYouWantToWithdraw : depleteTime?.status == 'For Life' ? 'forever, as the withdrawal rate is lower than the monthly gains' : depleteTime?.status}`}
                                                </span>
                                                {!calculatorType.includes('SWP') ? ' years' : ''}</>
                                    }
                                </APText>
                            </APRow>
                        </APColumn>
                    </APColumn>
                </APExpanded>
            </div>
            <APSizedBox height="8px" />
            <APText variant="paragraph-xsmall" >Calculators are for illustrations only and do not represent actual returns.<br />
                Mutual Fund investments are subject to market risks; read all scheme-related documents carefully.</APText>
            <div ref={in5Ref} style={{ display: "none" }}>
                <APRow>
                    <APText variant="paragraph-small" style={{ color: APPalette["grey-500"] }}>Powered by&nbsp;</APText>
                    <APImage src='/assetplus/aplight.svg' height="10px" style={{ marginTop: "2px" }} />
                </APRow>
            </div>
        </div >



    )
}


const getOrdinal = (number: number) => {
    // Handle special cases for 11, 12, and 13 separately
    if (number % 100 === 11 || number % 100 === 12 || number % 100 === 13) {
        return `${number}th`;
    }
    switch (number % 10) {
        case 1:

            return `${number}st`;
        case 2:
            return `${number}nd`;
        case 3:
            return `${number}rd`;
        default:
            return `${number}th`;
    }
}
